import React, { useState } from "react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { toast } from "sonner";
import {
  FileText,
  Calculator,
  Building2,
  ClipboardCheck,
  BookOpen,
  TrendingUp,
  Phone,
  Mail,
  ArrowRight,
  CheckCircle2,
  XCircle,
  Calendar,
  Send,
  MessageCircle,
  UtensilsCrossed,
  Store,
  ShoppingCart,
  Factory,
  HardHat,
  Rocket,
  Briefcase,
  Stethoscope,
  Menu,
  X,
  Award,
  Users,
  Trophy,
  Clock
} from "lucide-react";
import { servicesData, contactInfo, submitContactForm, bookAppointment, submitServiceRequest } from "../mock";
import WhatsAppButton from "../components/WhatsAppButton";

const Home = () => {
  const [contactForm, setContactForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [appointmentForm, setAppointmentForm] = useState({ name: "", email: "", phone: "", date: "", time: "", service: "" });
  const [serviceRequestForm, setServiceRequestForm] = useState({ name: "", email: "", phone: "", service: "", details: "" });
  const [loading, setLoading] = useState({ contact: false, appointment: false, service: false });
  const [dialogOpen, setDialogOpen] = useState({ appointment: false, service: false });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const iconMap = {
    FileText,
    Calculator,
    Building2,
    ClipboardCheck,
    BookOpen,
    TrendingUp
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setLoading({ ...loading, contact: true });
    try {
      const result = await submitContactForm(contactForm);
      toast.success(result.message);
      setContactForm({ name: "", email: "", phone: "", message: "" });
    } catch (error) {
      toast.error(error.message || "Something went wrong. Please try again.");
    } finally {
      setLoading({ ...loading, contact: false });
    }
  };

  const handleAppointmentSubmit = async (e) => {
    e.preventDefault();
    setLoading({ ...loading, appointment: true });
    try {
      const result = await bookAppointment(appointmentForm);
      toast.success(result.message);
      setAppointmentForm({ name: "", email: "", phone: "", date: "", time: "", service: "" });
      setDialogOpen({ ...dialogOpen, appointment: false });
    } catch (error) {
      toast.error(error.message || "Something went wrong. Please try again.");
    } finally {
      setLoading({ ...loading, appointment: false });
    }
  };

  const handleServiceRequestSubmit = async (e) => {
    e.preventDefault();
    setLoading({ ...loading, service: true });
    try {
      const result = await submitServiceRequest(serviceRequestForm);
      toast.success(result.message);
      setServiceRequestForm({ name: "", email: "", phone: "", service: "", details: "" });
      setDialogOpen({ ...dialogOpen, service: false });
    } catch (error) {
      toast.error(error.message || "Something went wrong. Please try again.");
    } finally {
      setLoading({ ...loading, service: false });
    }
  };

  return (
    <div className="min-h-screen bg-brand-white">
      {/* Header */}
      <header className="fixed top-0 w-full bg-white border-b border-brand-border shadow-sm z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="Medacc Consultancy" className="h-16 w-auto" data-testid="header-logo" />
            <span className="text-xl font-bold text-brand-navy">Medacc Consultancy</span>
          </div>
          <nav className="hidden lg:flex items-center gap-8">
            <a href="#services" className="text-brand-navy/80 hover:text-brand-orange transition-colors font-medium">Services</a>
            <a href="#about" className="text-brand-navy/80 hover:text-brand-orange transition-colors font-medium">About</a>
            <a href="/tax-tools" data-testid="nav-tax-tools-link" className="text-brand-navy/80 hover:text-brand-orange transition-colors font-medium">Tax Tools</a>
            <a href="#contact" className="text-brand-navy/80 hover:text-brand-orange transition-colors font-medium">Contact</a>
            <Dialog open={dialogOpen.appointment} onOpenChange={(open) => setDialogOpen({ ...dialogOpen, appointment: open })}>
              <DialogTrigger asChild>
                <Button className="bg-brand-orange hover:bg-brand-orange-dark text-white transition-colors">Book Consultation</Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>Book an Appointment</DialogTitle>
                  <DialogDescription>Schedule a consultation with our tax experts</DialogDescription>
                </DialogHeader>
                <form onSubmit={handleAppointmentSubmit} className="space-y-4 mt-4">
                  <div>
                    <Label htmlFor="apt-name">Full Name *</Label>
                    <Input
                      id="apt-name"
                      required
                      value={appointmentForm.name}
                      onChange={(e) => setAppointmentForm({ ...appointmentForm, name: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label htmlFor="apt-email">Email *</Label>
                    <Input
                      id="apt-email"
                      type="email"
                      required
                      value={appointmentForm.email}
                      onChange={(e) => setAppointmentForm({ ...appointmentForm, email: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label htmlFor="apt-phone">Phone *</Label>
                    <Input
                      id="apt-phone"
                      required
                      value={appointmentForm.phone}
                      onChange={(e) => setAppointmentForm({ ...appointmentForm, phone: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label htmlFor="apt-service">Service Type *</Label>
                    <Select value={appointmentForm.service} onValueChange={(value) => setAppointmentForm({ ...appointmentForm, service: value })}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a service" />
                      </SelectTrigger>
                      <SelectContent>
                        {servicesData.map((service) => (
                          <SelectItem key={service.id} value={service.title}>{service.title}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="apt-date">Preferred Date *</Label>
                      <Input
                        id="apt-date"
                        type="date"
                        required
                        value={appointmentForm.date}
                        onChange={(e) => setAppointmentForm({ ...appointmentForm, date: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label htmlFor="apt-time">Preferred Time *</Label>
                      <Input
                        id="apt-time"
                        type="time"
                        required
                        value={appointmentForm.time}
                        onChange={(e) => setAppointmentForm({ ...appointmentForm, time: e.target.value })}
                      />
                    </div>
                  </div>
                  <Button type="submit" className="w-full bg-brand-orange hover:bg-brand-orange-dark text-white" disabled={loading.appointment}>
                    {loading.appointment ? "Booking..." : "Confirm Appointment"}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </nav>
          <button
            className="lg:hidden text-brand-navy p-2"
            data-testid="mobile-menu-btn"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
        {mobileMenuOpen && (
          <div className="lg:hidden bg-white border-t border-brand-border px-4 py-4 space-y-1" data-testid="mobile-menu">
            {[["#services", "Services"], ["#about", "About"], ["/tax-tools", "Tax Tools"], ["#contact", "Contact"]].map(([href, label]) => (
              <a
                key={href}
                href={href}
                onClick={() => setMobileMenuOpen(false)}
                className="block py-2.5 text-brand-navy/80 hover:text-brand-orange transition-colors font-medium"
                data-testid={`mobile-nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {label}
              </a>
            ))}
            <Button
              className="w-full bg-brand-orange hover:bg-brand-orange-dark text-white mt-2"
              data-testid="mobile-book-consultation-btn"
              onClick={() => {
                setMobileMenuOpen(false);
                setDialogOpen({ ...dialogOpen, appointment: true });
              }}
            >
              Book Consultation
            </Button>
          </div>
        )}
      </header>

      {/* Hero Section - Navy with photo bleed */}
      <section className="relative pt-32 pb-20 px-4 bg-brand-navy overflow-hidden" data-testid="hero-section">
        {/* Full-width photo bleed with smooth navy gradient overlay */}
        <div className="absolute inset-0 hidden lg:block">
          <img
            src="https://customer-assets-jai6qajn.emergentagent.net/job_medacc-ca-services/artifacts/9k7hv77b_Medacc03.png"
            alt=""
            aria-hidden="true"
            className="w-full h-full object-cover object-center"
            style={{
              maskImage: 'linear-gradient(to right, transparent 0%, black 60%)',
              WebkitMaskImage: 'linear-gradient(to right, transparent 0%, black 60%)',
              maskRepeat: 'no-repeat',
              WebkitMaskRepeat: 'no-repeat'
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-brand-navy from-10% via-brand-navy/40 via-50% to-brand-navy/0" />
        </div>

        <div className="container mx-auto relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <div className="inline-block px-4 py-2 bg-brand-orange/15 border border-brand-orange/30 rounded-full">
                <span className="text-brand-orange font-semibold text-sm">Trusted Tax & Financial Consultants</span>
              </div>
              <h1 className="text-3xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight [text-shadow:_0_6px_24px_rgba(0,0,0,0.9),_0_2px_6px_rgba(0,0,0,0.7)]">
                Helping Businesses Stay <span className="text-brand-orange">Compliant</span>, Save Tax &amp; Grow
              </h1>
              <p className="text-base sm:text-lg text-white/90 leading-relaxed max-w-xl [text-shadow:_0_3px_14px_rgba(0,0,0,0.8),_0_1px_4px_rgba(0,0,0,0.6)]">
                GST, Taxation, Accounting, Audit, Compliance and Business Advisory services tailored for startups, SMEs and growing businesses.
              </p>
              <div className="flex flex-wrap gap-4 pt-2">
                <Button size="lg" data-testid="hero-get-started-btn" className="bg-brand-orange hover:bg-brand-orange-dark text-white transition-colors group" onClick={() => setDialogOpen({ ...dialogOpen, appointment: true })}>
                  Book Your Free Consultation <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Button>
                <a href="#services">
                  <Button size="lg" variant="outline" className="border-white/50 text-white hover:bg-white/10 hover:text-white bg-transparent" data-testid="hero-services-btn">
                    Our Services
                  </Button>
                </a>
                <Dialog open={dialogOpen.service} onOpenChange={(open) => setDialogOpen({ ...dialogOpen, service: open })}>
                  <DialogContent className="max-w-md">
                    <DialogHeader>
                      <DialogTitle>Request a Service</DialogTitle>
                      <DialogDescription>Tell us what you need and we'll get back to you</DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleServiceRequestSubmit} className="space-y-4 mt-4">
                      <div>
                        <Label htmlFor="srv-name">Full Name *</Label>
                        <Input
                          id="srv-name"
                          required
                          value={serviceRequestForm.name}
                          onChange={(e) => setServiceRequestForm({ ...serviceRequestForm, name: e.target.value })}
                        />
                      </div>
                      <div>
                        <Label htmlFor="srv-email">Email *</Label>
                        <Input
                          id="srv-email"
                          type="email"
                          required
                          value={serviceRequestForm.email}
                          onChange={(e) => setServiceRequestForm({ ...serviceRequestForm, email: e.target.value })}
                        />
                      </div>
                      <div>
                        <Label htmlFor="srv-phone">Phone *</Label>
                        <Input
                          id="srv-phone"
                          required
                          value={serviceRequestForm.phone}
                          onChange={(e) => setServiceRequestForm({ ...serviceRequestForm, phone: e.target.value })}
                        />
                      </div>
                      <div>
                        <Label htmlFor="srv-service">Service Needed *</Label>
                        <Select value={serviceRequestForm.service} onValueChange={(value) => setServiceRequestForm({ ...serviceRequestForm, service: value })}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select a service" />
                          </SelectTrigger>
                          <SelectContent>
                            {servicesData.map((service) => (
                              <SelectItem key={service.id} value={service.title}>{service.title}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label htmlFor="srv-details">Additional Details</Label>
                        <Textarea
                          id="srv-details"
                          rows={3}
                          value={serviceRequestForm.details}
                          onChange={(e) => setServiceRequestForm({ ...serviceRequestForm, details: e.target.value })}
                          placeholder="Tell us more about your requirements..."
                        />
                      </div>
                      <Button type="submit" className="w-full bg-brand-orange hover:bg-brand-orange-dark text-white" disabled={loading.service}>
                        {loading.service ? "Submitting..." : "Submit Request"}
                      </Button>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
              <div className="flex items-center gap-8 pt-4">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-brand-orange" />
                  <span className="text-white font-bold text-sm [text-shadow:_0_2px_8px_rgba(0,0,0,0.7)]">Expert CA Team</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-brand-orange" />
                  <span className="text-white font-bold text-sm [text-shadow:_0_2px_8px_rgba(0,0,0,0.7)]">100% Compliance</span>
                </div>
              </div>
            </div>
            {/* Mobile-only inline image */}
            <div className="lg:hidden relative rounded-2xl overflow-hidden shadow-xl border border-white/10">
              <img
                src="https://customer-assets-jai6qajn.emergentagent.net/job_medacc-ca-services/artifacts/9k7hv77b_Medacc03.png"
                alt="Medacc Consultancy - Financial Overview Dashboard"
                className="w-full h-auto object-cover"
                data-testid="hero-image"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Stats / Achievements Section - overlapping glass card */}
      <section className="relative px-4 -mt-12 z-10" data-testid="stats-section">
        <div className="container mx-auto">
          <div className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-[0_20px_60px_-15px_rgba(16,43,78,0.35)] border border-white ring-1 ring-brand-border/60 py-6 px-6 md:px-8">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6 md:gap-4">
              {[
                { value: "10+", label: "Years of Experience", Icon: Award },
                { value: "100+", label: "Businesses Served", Icon: Users },
                { value: "₹800 Cr+", label: "Transactions Managed", Icon: TrendingUp },
                { value: "98%", label: "Client Retention", Icon: Trophy },
                { value: "24-Hour", label: "Response Time", Icon: Clock },
              ].map((stat) => (
                <div
                  key={stat.label}
                  className="flex items-center gap-3 group"
                  data-testid={`stat-${stat.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <div className="relative w-11 h-11 rounded-xl bg-brand-navy/5 border border-brand-navy/10 flex items-center justify-center shrink-0 group-hover:-translate-y-0.5 transition-transform">
                    <stat.Icon className="w-5 h-5 text-brand-navy" strokeWidth={2} />
                  </div>
                  <div className="min-w-0">
                    <div className="text-lg lg:text-xl font-bold text-brand-navy leading-tight whitespace-nowrap">{stat.value}</div>
                    <div className="text-xs text-brand-muted leading-tight">{stat.label}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-14 sm:py-20 px-4" style={{ backgroundColor: '#EAE5DD' }}>
        <div className="container mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-4xl font-bold text-brand-navy mb-4">Our Services</h2>
            <p className="text-xl text-brand-muted">Comprehensive tax and financial solutions designed to help your business thrive</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {servicesData.map((service) => {
              const Icon = iconMap[service.icon];
              return (
                <Card key={service.id} className="group relative bg-white rounded-2xl shadow-md hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 border border-brand-border border-t-4 border-t-brand-orange overflow-visible">
                  <CardHeader className="pb-3">
                    {/* Floating icon - navy with orange highlight */}
                    <div className="relative w-14 h-14 bg-brand-navy/5 border border-brand-navy/10 rounded-2xl flex items-center justify-center mb-5 shadow-sm group-hover:-translate-y-1 group-hover:shadow-md transition-all duration-300">
                      <Icon className="w-6 h-6 text-brand-navy" strokeWidth={2} />
                    </div>
                    <CardTitle className="text-xl text-brand-navy">{service.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-brand-muted leading-relaxed">{service.description}</CardDescription>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* About / Why Choose Section - Comparison */}
      <section id="about" className="py-14 sm:py-20 px-4 bg-brand-white" data-testid="why-choose-section">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-10">
            <h2 className="text-4xl font-bold text-brand-navy mb-3">Why Choose Medacc?</h2>
            <p className="text-lg text-brand-muted max-w-2xl mx-auto">See how our approach differs from the traditional accounting experience</p>
          </div>
          <div className="grid lg:grid-cols-2 gap-10 items-stretch">
            <div className="h-full">
              <img
                src="/why-choose-medacc.png"
                alt="Why Choose Medacc — strategic guidance, strong foundation and clear financial direction"
                className="w-full h-full object-cover rounded-2xl shadow-lg"
                data-testid="why-choose-image"
              />
            </div>
            <div className="flex flex-col justify-center">
              <div className="bg-white rounded-2xl shadow-md border border-brand-border overflow-hidden">
                <div className="grid grid-cols-2">
                  <div className="p-3 sm:p-6 bg-brand-grey border-b border-brand-border">
                    <h3 className="font-bold text-brand-muted text-xs sm:text-lg text-center">Traditional Accountant</h3>
                  </div>
                  <div className="p-3 sm:p-6 bg-brand-navy border-b border-brand-navy">
                    <h3 className="font-bold text-white text-xs sm:text-lg text-center">Medacc Consultancy</h3>
                  </div>
                </div>
                {[
                  { old: "Reactive advice", medacc: "Proactive financial guidance" },
                  { old: "Last-minute tax filing", medacc: "Year-round planning" },
                  { old: "Slow communication", medacc: "Dedicated support" },
                  { old: "Manual paperwork", medacc: "Digital-first processes" },
                  { old: "Limited business advice", medacc: "End-to-end business consulting" },
                ].map((row, i) => (
                  <div key={row.old} className="grid grid-cols-2" data-testid={`comparison-row-${i + 1}`}>
                    <div className="p-3 sm:p-5 flex items-center gap-2 sm:gap-3 border-b border-brand-border bg-white">
                      <XCircle className="w-4 h-4 sm:w-5 sm:h-5 text-red-400 shrink-0" />
                      <span className="text-brand-muted text-xs sm:text-base leading-snug">{row.old}</span>
                    </div>
                    <div className="p-3 sm:p-5 flex items-center gap-2 sm:gap-3 border-b border-brand-border bg-brand-orange/[0.08]">
                      <CheckCircle2 className="w-4 h-4 sm:w-5 sm:h-5 text-brand-orange shrink-0" />
                      <span className="text-brand-navy font-medium text-xs sm:text-base leading-snug">{row.medacc}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Industries We Serve Section */}
      <section className="py-14 sm:py-20 px-4 bg-brand-navy" data-testid="industries-section">
        <div className="container mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-4xl font-bold text-white mb-4">Industries We Serve</h2>
            <p className="text-lg text-white/70 max-w-2xl mx-auto">Specialized expertise across diverse business sectors</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {[
              { icon: UtensilsCrossed, label: "Restaurants & Cafés" },
              { icon: Store, label: "Retail Businesses" },
              { icon: ShoppingCart, label: "E-commerce" },
              { icon: Factory, label: "Manufacturing" },
              { icon: HardHat, label: "Construction" },
              { icon: Rocket, label: "Startups" },
              { icon: Briefcase, label: "Consultants & Freelancers" },
              { icon: Stethoscope, label: "Professionals (Doctors, Lawyers, Architects)" },
            ].map((industry) => {
              const IndustryIcon = industry.icon;
              return (
                <div
                  key={industry.label}
                  data-testid={`industry-${industry.label.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}
                  className="relative bg-white/[0.06] border border-white/15 rounded-2xl p-5 sm:p-10 text-center hover:border-brand-orange/70 hover:bg-white/[0.10] hover:-translate-y-1 transition-all duration-300 group backdrop-blur-md shadow-lg shadow-black/20 hover:shadow-xl hover:shadow-brand-orange/10 min-h-[150px] sm:min-h-[180px] flex flex-col items-center justify-center"
                >
                  <div className="relative w-16 h-16 mx-auto mb-5 bg-white/[0.08] border border-white/20 rounded-2xl flex items-center justify-center backdrop-blur-md group-hover:-translate-y-1 transition-transform duration-300">
                    <IndustryIcon className="w-7 h-7 text-white" strokeWidth={2} />
                  </div>
                  <p className="text-white font-semibold text-base leading-snug">{industry.label}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Compliance Calendar Section */}
      <section id="compliance-calendar" className="py-14 sm:py-20 px-4 bg-brand-grey" data-testid="compliance-calendar-section">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-14">
            <h2 className="text-4xl font-bold text-brand-navy mb-4">Compliance Calendar</h2>
            <p className="text-lg text-brand-muted max-w-2xl mx-auto">Never miss a deadline — key compliance due dates at a glance</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                title: "GST Returns",
                icon: FileText,
                items: [
                  { due: "11th", freq: "Monthly", desc: "GSTR-1 (Outward supplies)" },
                  { due: "20th", freq: "Monthly", desc: "GSTR-3B (Summary return & tax payment)" },
                  { due: "18th", freq: "Quarterly", desc: "CMP-08 (Composition scheme)" },
                ],
              },
              {
                title: "TDS Filing",
                icon: ClipboardCheck,
                items: [
                  { due: "7th", freq: "Monthly", desc: "TDS/TCS payment for previous month" },
                  { due: "31st", freq: "Quarterly", desc: "TDS returns (Jul / Oct / Jan / May)" },
                  { due: "15th", freq: "Quarterly", desc: "TDS certificates (Form 16A)" },
                ],
              },
              {
                title: "Income Tax Returns",
                icon: Calculator,
                items: [
                  { due: "31 Jul", freq: "Yearly", desc: "ITR for individuals & non-audit cases" },
                  { due: "31 Oct", freq: "Yearly", desc: "ITR for audit cases & companies" },
                  { due: "30 Sep", freq: "Yearly", desc: "Tax audit report filing" },
                ],
              },
              {
                title: "ROC Filings",
                icon: Building2,
                items: [
                  { due: "30 Sep", freq: "Yearly", desc: "DIR-3 KYC (Directors)" },
                  { due: "30 Oct", freq: "Yearly", desc: "AOC-4 (Financial statements)" },
                  { due: "29 Nov", freq: "Yearly", desc: "MGT-7 (Annual return)" },
                ],
              },
              {
                title: "Advance Tax",
                icon: TrendingUp,
                items: [
                  { due: "15 Jun", freq: "Q1", desc: "1st installment — 15% of tax" },
                  { due: "15 Sep", freq: "Q2", desc: "2nd installment — 45% cumulative" },
                  { due: "15 Dec", freq: "Q3", desc: "3rd installment — 75% cumulative" },
                  { due: "15 Mar", freq: "Q4", desc: "Final installment — 100%" },
                ],
              },
            ].map((cat) => {
              const CatIcon = cat.icon;
              return (
                <Card key={cat.title} className="bg-white rounded-2xl border border-brand-border shadow-md hover:shadow-xl hover:-translate-y-1 transition-all duration-300 border-t-4 border-t-brand-orange" data-testid={`calendar-${cat.title.toLowerCase().replace(/\s+/g, "-")}`}>
                  <CardHeader className="pb-3">
                    <div className="flex items-center gap-3">
                      <div className="relative w-11 h-11 rounded-xl flex items-center justify-center bg-brand-navy/5 border border-brand-navy/10">
                        <CatIcon className="w-5 h-5 text-brand-navy" strokeWidth={2} />
                      </div>
                      <CardTitle className="text-lg text-brand-navy">{cat.title}</CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {cat.items.map((item) => (
                      <div key={item.desc} className="flex items-start gap-3">
                        <div className="bg-brand-navy text-white rounded-md px-2 py-1 text-xs font-bold min-w-[52px] text-center shrink-0">{item.due}</div>
                        <div>
                          <p className="text-sm text-brand-navy font-medium leading-snug">{item.desc}</p>
                          <p className="text-xs text-brand-muted">{item.freq}</p>
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              );
            })}
            <div className="bg-brand-navy border border-brand-navy rounded-xl p-6 flex flex-col justify-center items-center text-center" data-testid="calendar-cta-card">
              <Calendar className="w-10 h-10 text-brand-orange mb-4" />
              <h3 className="text-white font-bold text-lg mb-2">Never Miss a Deadline</h3>
              <p className="text-white/70 text-sm mb-4">We track every due date for you, so you can focus on your business.</p>
              <Button
                className="bg-brand-orange text-white hover:bg-brand-orange-dark border-0"
                data-testid="calendar-book-btn"
                onClick={() => setDialogOpen({ ...dialogOpen, appointment: true })}
              >
                Book a Consultation
              </Button>
            </div>
          </div>
          <p className="text-xs text-brand-muted text-center mt-8">Due dates as per current regulations and subject to change by government notifications. Dates falling on holidays may be extended.</p>
        </div>
      </section>

      {/* Combined FAQ + Contact Section - side by side */}
      <section id="contact" className="py-14 sm:py-20 px-4 bg-brand-white" data-testid="faq-contact-section">
        <div className="container mx-auto max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* FAQ left */}
            <div id="faq" data-testid="faq-section">
              <div className="mb-10">
                <h2 className="text-4xl font-bold text-brand-navy mb-3">Frequently Asked Questions</h2>
                <p className="text-lg text-brand-muted">Quick answers to common queries about our services</p>
              </div>
              <Accordion type="single" collapsible className="space-y-3">
            {[
              {
                q: "Do startups need GST registration?",
                a: "GST registration is mandatory once your annual turnover crosses ₹40 lakh for goods (₹20 lakh for services), or if you sell inter-state or through e-commerce platforms. Many startups also register voluntarily to claim input tax credit and build credibility with vendors and clients. We can assess your situation and handle the entire registration process for you.",
              },
              {
                q: "Which businesses require audits?",
                a: "A tax audit is required if your business turnover exceeds ₹1 crore (₹10 crore if most transactions are digital) or professional receipts exceed ₹50 lakh. All companies registered under the Companies Act require a statutory audit regardless of turnover. LLPs need audits above ₹40 lakh turnover or ₹25 lakh contribution. We'll tell you exactly what applies to your business.",
              },
              {
                q: "What documents are required for Income Tax filing?",
                a: "Typically: PAN, Aadhaar, Form 16 (for salaried), bank statements, investment proofs (80C, 80D etc.), home loan interest certificates, capital gains statements, and business financials if applicable. Share what you have — we'll prepare a personalised checklist and take care of the rest.",
              },
              {
                q: "Can Medacc manage monthly bookkeeping?",
                a: "Yes! Monthly bookkeeping is one of our core services. We manage your books digitally — recording transactions, reconciling bank statements, tracking receivables/payables, and giving you clear monthly reports. You focus on growing the business; we keep your books accurate and compliance-ready.",
              },
              {
                q: "Do you offer virtual consultations?",
                a: "Absolutely. We work with clients across India through video calls, phone, WhatsApp and email. Documents are shared securely online, so your location is never a barrier. You can book a consultation right from this website and choose a time that suits you.",
              },
              {
                q: "How quickly can I register a company?",
                a: "A private limited company typically takes 7-10 working days once documents are ready — including name approval, DSC, incorporation and PAN/TAN. LLPs take a similar time; proprietorships and partnerships are usually faster. We handle the entire process end-to-end and keep you updated at every step.",
              },
            ].map((faq, i) => (
              <AccordionItem
                key={faq.q}
                value={`faq-${i}`}
                className="bg-white border border-brand-border rounded-lg px-5 data-[state=open]:border-brand-orange data-[state=open]:shadow-sm"
                data-testid={`faq-item-${i + 1}`}
              >
                <AccordionTrigger className="text-left font-semibold text-brand-navy hover:text-brand-orange hover:no-underline">
                  {faq.q}
                </AccordionTrigger>
                <AccordionContent className="text-brand-muted leading-relaxed">
                  {faq.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
            </div>

            {/* Get In Touch right */}
            <div>
              <div className="mb-10">
                <h2 className="text-4xl font-bold text-brand-navy mb-3">Get In Touch</h2>
                <p className="text-lg text-brand-muted">Have questions? We're here to help!</p>
              </div>
              <div className="space-y-6">
                <Card className="bg-white border border-brand-border">
              <CardHeader>
                <CardTitle className="text-brand-navy">Send us a Message</CardTitle>
                <CardDescription>Fill out the form and we'll get back to you shortly</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleContactSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="name">Full Name *</Label>
                    <Input
                      id="name"
                      required
                      value={contactForm.name}
                      onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                      placeholder="John Doe"
                    />
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="email">Email *</Label>
                      <Input
                        id="email"
                        type="email"
                        required
                        value={contactForm.email}
                        onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                        placeholder="john@example.com"
                      />
                    </div>
                    <div>
                      <Label htmlFor="phone">Phone *</Label>
                      <Input
                        id="phone"
                        required
                        value={contactForm.phone}
                        onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                        placeholder="9876543210"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="message">Message *</Label>
                    <Textarea
                      id="message"
                      required
                      rows={5}
                      value={contactForm.message}
                      onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                      placeholder="Tell us how we can help..."
                    />
                  </div>
                  <Button type="submit" className="w-full bg-brand-orange hover:bg-brand-orange-dark text-white" disabled={loading.contact}>
                    {loading.contact ? "Sending..." : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Send Message
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Orange → Navy blended CTA Banner */}
      <section
        className="relative py-12 sm:py-16 px-4 overflow-hidden"
        style={{
          background: 'linear-gradient(120deg, #F36F21 0%, #EC6820 18%, #D95B10 35%, #B25025 50%, #6E4442 62%, #3E3F52 74%, #1B3D6B 87%, #102B4E 100%)'
        }}
        data-testid="cta-section"
      >
        <div className="container mx-auto relative">
          <div className="grid md:grid-cols-[1fr_auto] items-center gap-6 md:gap-8">
            {/* Text (aligned with footer left edge) */}
            <div className="text-white">
              <div className="flex items-start sm:items-center gap-3 mb-2">
                <span className="inline-flex w-10 h-10 rounded-full bg-white/15 border border-white/40 items-center justify-center backdrop-blur-sm shrink-0">
                  <Calendar className="w-5 h-5 text-white" strokeWidth={2} />
                </span>
                <h2 className="text-2xl sm:text-4xl font-bold leading-tight [text-shadow:_0_2px_10px_rgba(0,0,0,0.25)]">
                  Ready to Simplify Your Business Finances?
                </h2>
              </div>
              <p className="text-white/90 text-sm sm:text-lg">
                Book your free consultation today and let our experts help your business grow.
              </p>
            </div>
            {/* CTA button */}
            <Button
              size="lg"
              className="bg-white text-brand-orange hover:bg-brand-navy hover:text-white font-semibold shadow-lg h-12 px-6 text-base transition-colors group whitespace-nowrap w-full md:w-auto"
              onClick={() => setDialogOpen({ ...dialogOpen, appointment: true })}
              data-testid="cta-book-btn"
            >
              Book Free Consultation
              <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white text-brand-navy py-8 px-4 border-t border-brand-border">
        <div className="container mx-auto">
          <div className="grid md:grid-cols-3 gap-8 mb-5 items-start">
            <div>
              <div className="flex items-center gap-3 mb-3">
                <img src="/logo.png" alt="Medacc Consultancy" className="h-16 w-auto" data-testid="footer-logo" />
                <span className="text-2xl font-bold text-brand-navy">Medacc Consultancy</span>
              </div>
              <p className="text-brand-navy/70 leading-relaxed text-lg">
                Your trusted partner for tax compliance and financial advisory services.
              </p>
            </div>
            <div>
              <h3 className="font-semibold text-xl mb-3 text-brand-navy">Quick Links</h3>
              <ul className="space-y-1.5 text-lg">
                <li><a href="#services" className="text-brand-navy/70 hover:text-brand-orange transition-colors">Services</a></li>
                <li><a href="#about" className="text-brand-navy/70 hover:text-brand-orange transition-colors">About Us</a></li>
                <li><a href="/tax-tools" className="text-brand-navy/70 hover:text-brand-orange transition-colors">Tax Tools</a></li>
                <li><a href="#contact" className="text-brand-navy/70 hover:text-brand-orange transition-colors">Contact</a></li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold text-xl mb-3 text-brand-navy">Contact Us</h3>
              <ul className="space-y-1.5 text-lg">
                <li className="flex items-center gap-2 text-brand-navy/70">
                  <Mail className="w-5 h-5 text-brand-orange" />
                  <a href={`mailto:${contactInfo.email}`} className="hover:text-brand-orange transition-colors">
                    {contactInfo.email}
                  </a>
                </li>
                <li className="flex items-center gap-2 text-brand-navy/70">
                  <Phone className="w-5 h-5 text-brand-orange" />
                  <a href={`tel:${contactInfo.phone}`} className="hover:text-brand-orange transition-colors">
                    {contactInfo.phone}
                  </a>
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-brand-border pt-4 text-center text-brand-navy/60 text-base">
            <p>&copy; {new Date().getFullYear()} Medacc Consultancy. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* WhatsApp Floating Button */}
      <WhatsAppButton />
    </div>
  );
};

export default Home;
